/**
 * FeedbackForm — shared Feedback-Komponente für alle Vimaana-Web-Apps.
 *
 * Bewusst schlank gehalten (Stand 14.06.2026): keine Kategorie-Wahl mehr
 * (Bug vs. Feature „ergibt sich" aus dem Text und der Triage), kein
 * separates Titel-Feld. Stattdessen zwei Eingaben:
 *
 *   1. „Auf welche Seite/welchen Bereich bezieht sich dein Feedback?"
 *      — ein Selektor aus der App-eigenen Bereichsliste (`pages`-Prop).
 *      Das ersetzt die Kategorie und liefert dem Baumeister sofort den
 *      Umsetzungs-Kontext (wo muss ich hinschauen?).
 *   2. Ein Freitextfeld.
 *
 * Der Titel für die Inbox-Übersicht wird serverseitig aus der ersten
 * Zeile der Beschreibung abgeleitet (Backend), die Komponente sendet
 * keinen Titel.
 *
 * Die App entscheidet weiterhin selbst, wie gesendet wird (Sidecar im
 * Desktop, direkt zu api.vimaanagarten.de in den PWAs) — sie bekommt
 * einen `onSubmit`-Callback mit normalisiertem Payload.
 *
 * Optional ein `onUploadScreenshot`-Callback — wenn gesetzt, zeigt der
 * Form den „Screenshot anhängen"-Knopf und lädt html2canvas dynamisch
 * von CDN.
 */

import { useState } from "react";

/** Ein wählbarer Bereich der App. `value` landet als page_path. */
export interface FeedbackPage {
  value: string;
  label: string;
}

export interface FeedbackFormPayload {
  /** Wird nicht mehr im Form erhoben — Backend leitet aus beschreibung ab. */
  titel?: string;
  beschreibung: string;
  /** Der gewählte Bereich (value aus `pages`). */
  page_path: string;
  screenshot_url?: string;
  context: Record<string, unknown>;
}

export interface FeedbackFormProps {
  /** Bereichsliste der App, aus der der Bezug gewählt wird. */
  pages: FeedbackPage[];
  /** Vorausgewählter Bereich (z. B. die zuletzt offene Seite). */
  defaultPage?: string;
  /** Wird beim Submit gerufen. Liefert true/false zurück um Erfolg/Fehler anzuzeigen. */
  onSubmit: (p: FeedbackFormPayload) => Promise<{ ok: boolean; error?: string }>;
  /** Optional: Screenshot-Upload. Wenn nicht gesetzt, ist der Screenshot-Knopf aus. */
  onUploadScreenshot?: (blob: Blob) => Promise<{ screenshot_url?: string; error?: string }>;
  /** Wird nach erfolgreichem Submit gerufen (z. B. Seite/Modal schließen). */
  onDone?: () => void;
  /** Bestätigungstext nach erfolgreichem Senden. */
  successText?: string;
}

export function FeedbackForm({
  pages,
  defaultPage,
  onSubmit,
  onUploadScreenshot,
  onDone,
  successText = "Danke, ist eingegangen — wir nehmen es in die Weiterentwicklung auf.",
}: FeedbackFormProps) {
  const initialPage = defaultPage && pages.some((p) => p.value === defaultPage)
    ? defaultPage
    : pages[0]?.value ?? "";
  const [page, setPage] = useState<string>(initialPage);
  const [beschreibung, setBeschreibung] = useState("");
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [screenshotBusy, setScreenshotBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ bereich: string; text: string } | null>(null);

  const captureScreenshot = async () => {
    if (!onUploadScreenshot) return;
    setErr(null);
    setScreenshotBusy(true);
    try {
      const html2canvas = await loadHtml2Canvas();
      await new Promise((r) => setTimeout(r, 30));
      const canvas = await html2canvas(document.body, {
        backgroundColor: "#0a0d09",
        scale: Math.min(window.devicePixelRatio || 1, 2),
        logging: false,
      });
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png"),
      );
      if (!blob) {
        setErr("Screenshot konnte nicht erzeugt werden.");
        return;
      }
      setScreenshotBlob(blob);
      setScreenshotPreview(URL.createObjectURL(blob));
    } catch (e) {
      setErr(`Screenshot fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setScreenshotBusy(false);
    }
  };

  const dropScreenshot = () => {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotBlob(null);
    setScreenshotPreview(null);
  };

  const resetForm = () => {
    setBeschreibung("");
    setPage(initialPage);
    dropScreenshot();
    setErr(null);
    setDone(null);
  };

  const submit = async () => {
    setErr(null);
    if (!beschreibung.trim()) return setErr("Bitte beschreibe dein Feedback.");

    setSubmitBusy(true);
    try {
      let screenshotUrl: string | undefined;
      if (screenshotBlob && onUploadScreenshot) {
        const up = await onUploadScreenshot(screenshotBlob);
        if (up.screenshot_url) screenshotUrl = up.screenshot_url;
      }
      const bereichLabel = pages.find((p) => p.value === page)?.label ?? page;
      const res = await onSubmit({
        beschreibung: beschreibung.trim(),
        page_path: page,
        screenshot_url: screenshotUrl,
        context: {
          bezug_seite: bereichLabel,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        },
      });
      if (res.ok) {
        setDone({ bereich: bereichLabel, text: successText });
        setBeschreibung("");
        dropScreenshot();
      } else {
        setErr(res.error || "Feedback konnte nicht gespeichert werden.");
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitBusy(false);
    }
  };

  if (done) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem", textAlign: "center", padding: "1rem 0" }}>
        <div style={{ fontSize: 40, lineHeight: 1 }} aria-hidden>✓</div>
        <div style={{ color: "var(--ok, #9bd17a)", fontWeight: 700, fontSize: 16 }}>Feedback gesendet</div>
        <div
          style={{
            textAlign: "left",
            border: "1px solid var(--m-line, rgba(255,255,255,0.12))",
            borderRadius: 10,
            padding: "0.75rem 0.9rem",
            background: "var(--m-bg-3, rgba(255,255,255,0.04))",
          }}
        >
          <div className="muted small" style={{ marginBottom: 4 }}>Bezug: {done.bereich}</div>
        </div>
        <div className="muted small">{done.text}</div>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
          <button type="button" onClick={() => (onDone ? onDone() : resetForm())}>
            Fertig
          </button>
          <button type="button" onClick={resetForm}>
            Weiteres Feedback
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <span className="muted small">Auf welche Seite bezieht sich dein Feedback?</span>
        <select
          value={page}
          onChange={(e) => setPage(e.target.value)}
          disabled={submitBusy}
        >
          {pages.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <span className="muted small">Dein Feedback</span>
        <textarea
          rows={6}
          value={beschreibung}
          placeholder="Was ist dir aufgefallen? Was würdest du dir wünschen oder anders machen?"
          onChange={(e) => setBeschreibung(e.target.value)}
          disabled={submitBusy}
        />
      </label>

      {onUploadScreenshot ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <span className="muted small">Screenshot (optional)</span>
          {screenshotPreview ? (
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <img src={screenshotPreview} alt="" style={{ maxWidth: 220, maxHeight: 140 }} />
              <button type="button" onClick={dropScreenshot} disabled={submitBusy}>
                Entfernen
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={captureScreenshot}
              disabled={screenshotBusy || submitBusy}
            >
              {screenshotBusy ? "Aufnahme läuft …" : "Screenshot anhängen"}
            </button>
          )}
        </div>
      ) : null}

      {err ? <div style={{ color: "var(--err, #ff8d8d)" }}>{err}</div> : null}

      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
        <button type="button" onClick={submit} disabled={submitBusy}>
          {submitBusy ? "Sende …" : "Feedback senden"}
        </button>
      </div>
    </div>
  );
}

// ── html2canvas dynamisch von CDN laden ───────────────────────────────────────

declare global {
  interface Window {
    html2canvas?: (
      el: HTMLElement,
      opts?: { backgroundColor?: string; scale?: number; logging?: boolean },
    ) => Promise<HTMLCanvasElement>;
  }
}

async function loadHtml2Canvas(): Promise<NonNullable<Window["html2canvas"]>> {
  if (window.html2canvas) return window.html2canvas;
  await new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("html2canvas konnte nicht geladen werden"));
    document.head.appendChild(s);
  });
  if (!window.html2canvas) throw new Error("html2canvas nicht verfügbar");
  return window.html2canvas;
}
